import { useEffect } from 'react';
import type { EditorShortcutDto } from '@xshop/shared';
import { postToBuilder } from './editorChannel';

// The builder's shortcuts, heard inside the preview. The frame is a different origin, so the builder's
// own key listener never receives a press made here — and a merchant is inside the canvas from the moment
// they click the page, which is to say almost always. The canvas forwards the press and changes nothing
// itself: the draft belongs to the builder, so this asks in exactly the way the floating toolbar asks.

/** Where a keystroke means text, not a command; the same rule the builder's own listener applies. */
const inAField = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

// Reads one press as the act it stands for, or null for every key that is not a builder shortcut.
function shortcutOf(event: KeyboardEvent): EditorShortcutDto['action'] | null {
  if (!(event.metaKey || event.ctrlKey)) return null;
  const key = event.key.toLowerCase();
  if (key === 's') return 'save';
  if (key !== 'z') return null;
  return event.shiftKey ? 'redo' : 'undo';
}

// Forwards ⌘Z, ⇧⌘Z and ⌘S to the builder for as long as this page is being previewed inside one.
export function useEditorShortcuts(active: boolean): void {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const action = shortcutOf(event);
      if (!action || inAField(event.target)) return;
      // ⌘S would otherwise hand the merchant the browser's own "save this page" dialog over their store.
      event.preventDefault();
      postToBuilder({ type: 'shortcut', action });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);
}
